'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  label: string;
  path: string;
  icon: string;
  tooltip: string;
  badge?: number;
}

interface SidebarProps {
  isCollapsed?: boolean;
}

const Sidebar = ({ isCollapsed = false }: SidebarProps) => {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(isCollapsed);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const savedState = localStorage.getItem('sidebar-collapsed');
    if (savedState !== null) {
      setCollapsed(JSON.parse(savedState));
    }
  }, []);

  const toggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
  };

  const navigationItems: NavigationItem[] = [
    {
      label: 'Home',
      path: '/financial-close-command-center-home',
      icon: 'HomeIcon',
      tooltip: 'Financial Close Command Center - Month, Quarter, Year-end oversight',
    },
    {
      label: 'Live Mirror',
      path: '/live-mirror-dashboard',
      icon: 'ChartBarIcon',
      tooltip: 'Real-time financial close monitoring dashboard',
    },
    {
      label: 'Certification',
      path: '/certification-terminal',
      icon: 'ShieldCheckIcon',
      tooltip: 'Audit compliance terminal for data integrity verification',
    },
    {
      label: 'Anomalies',
      path: '/anomaly-management-hub',
      icon: 'ExclamationTriangleIcon',
      tooltip: 'Risk monitoring hub for financial drift detection',
      badge: 0,
    },
    {
      label: 'Connectivity',
      path: '/universal-connectivity-hub',
      icon: 'CircleStackIcon',
      tooltip: 'Universal ERP connectivity hub for heterogeneous landscapes',
    },
    {
      label: 'Admin',
      path: '/admin-settings-governance-hub',
      icon: 'Cog6ToothIcon',
      tooltip: 'System administration and governance oversight',
    },
    {
      label: 'Contact',
      path: '/contact-demo-hub',
      icon: 'EnvelopeIcon',
      tooltip: 'Contact sales and request sandbox demo',
    },
  ];

  if (!mounted) {
    return null;
  }

  return (
    <>
      {/* Desktop & Tablet Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen bg-primary border-r border-border shadow-lg transition-all duration-250 ease-out z-navigation hidden md:flex flex-col ${
          collapsed ? 'w-20' : 'w-60'
        }`}
      >
        {/* Logo Section */}
        <div className="flex items-center h-20 px-6 border-b border-border">
          <Link href="/live-mirror-dashboard" className="flex items-center gap-3 group">
            <div className="flex items-center justify-center w-10 h-10 bg-accent rounded-md shadow-md group-hover:shadow-lg transition-all duration-250">
              <span className="font-mono font-bold text-lg text-accent-foreground">MQ</span>
            </div>
            {!collapsed && (
              <div className="flex flex-col">
                <span className="font-mono font-semibold text-base text-foreground leading-tight">
                  MQYClose
                </span>
                <span className="font-sans text-xs text-text-secondary leading-tight">
                  Financial Command
                </span>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto">
          {navigationItems.map((item) => {
            const isActive = pathname === item.path;
            return (
              <Link
                key={item.path}
                href={item.path}
                title={collapsed ? item.tooltip : undefined}
                className={`flex items-center gap-3 px-3 py-3 rounded-md transition-all duration-250 ease-out group relative ${
                  isActive
                    ? 'bg-accent text-accent-foreground shadow-md'
                    : 'text-text-secondary hover:bg-secondary hover:text-foreground hover:shadow-sm'
                }`}
              >
                <Icon
                  name={item.icon as any}
                  size={20}
                  className={`flex-shrink-0 transition-all duration-250 ${
                    isActive ? 'text-accent-foreground' : 'text-text-secondary group-hover:text-foreground'
                  }`}
                />
                {!collapsed && (
                  <>
                    <span className="font-mono text-sm font-medium flex-1">{item.label}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-error text-error-foreground text-xs font-mono font-semibold rounded-full z-badge">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </>
                )}
                {collapsed && item.badge !== undefined && item.badge > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-error text-error-foreground text-[10px] font-mono font-semibold rounded-full z-badge">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Collapse Toggle */}
        <div className="px-3 py-4 border-t border-border">
          <button
            onClick={toggleCollapse}
            className="flex items-center justify-center w-full px-3 py-3 rounded-md bg-secondary text-secondary-foreground hover:bg-muted hover:shadow-sm transition-all duration-250 ease-out active:scale-98"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon
              name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
              size={20}
              className="text-text-secondary"
            />
            {!collapsed && <span className="ml-3 font-mono text-sm font-medium">Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 h-16 bg-primary border-t border-border shadow-xl z-navigation flex md:hidden">
        <div className="flex items-center justify-around w-full px-2">
          {navigationItems.map((item) => {
            const isActive = pathname === item.path;
            return (
              <Link
                key={item.path}
                href={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full px-2 py-2 transition-all duration-250 ease-out relative ${
                  isActive ? 'text-accent' : 'text-text-secondary'
                }`}
              >
                <div className="relative">
                  <Icon
                    name={item.icon as any}
                    size={24}
                    className={`transition-all duration-250 ${
                      isActive ? 'text-accent' : 'text-text-secondary'
                    }`}
                  />
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-4 px-1 bg-error text-error-foreground text-[10px] font-mono font-semibold rounded-full z-badge">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span
                  className={`font-mono text-[10px] font-medium mt-1 ${
                    isActive ? 'text-accent' : 'text-text-secondary'
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default Sidebar;